import { checkSession } from "@features/auth";
import { authClient } from "@features/auth/authClient";
import { AuthRoute } from "@features/auth/AuthRoute";
import { useAppForm } from "@features/form/useAppForm";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { revalidateLogic } from "@tanstack/react-form";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { toast } from "sonner";
import z from "zod";
import type { InvitationJoinProps } from "./AuthRoute";
import { InvitationJoinCheckbox } from "./InvitationJoinCheckbox";
import { SocialSignInAndUp } from "./SocialSignInUp";

type SignInPageProps = {
	email: string;
	onChangeEmail: React.Dispatch<React.SetStateAction<string>> | undefined;
	invitationJoin?: InvitationJoinProps;
};

export const SignInPage = (props: SignInPageProps) => {
	const { email, onChangeEmail, invitationJoin } = props;
	const navigate = useNavigate();
	const { show } = AuthRoute.useSearch();
	const LL = useI18nContext().LL.auth;

	const SignInSchema = useMemo(
		() =>
			z.object({
				email: z.email(),
				password: z.string().min(8, LL.FORM_FIELDS_PASSWORD_ERROR_MESSAGE()),
			}),
		[LL]
	);

	const form = useAppForm({
		defaultValues: {
			email: email,
			password: "",
		},
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: {
			onDynamic: SignInSchema,
		},
		onSubmit: async ({ value }) => {
			try {
				const res = await authClient.signIn.email(value);
				if (res.error) {
					if (res.error.code === "EMAIL_NOT_VERIFIED") {
						// Preserve the existing search params (invitationId, redirectToPath, ...).
						return navigate({ from: AuthRoute.fullPath, search: (prev) => ({ ...prev, show, verifyEmail: value.email }) });
					}
					throw res.error;
				}
				await checkSession();
			} catch (error: any) {
				console.error(error);
				toast.error("message" in error ? (error.message as string) : "Something went wrong, try again later");
			}
		},
	});

	return (
		<>
			<div className="m-auto w-full max-w-sm space-y-6">
				<div className="space-y-2 text-center">
					<h1 className="text-3xl font-bold font-breit">
						<T>{LL.SIGN_IN_TITLE()}</T>
					</h1>
					<p className="text-muted-foreground text-sm">
						<T>{LL.SIGN_IN_DESCRIPTION()}</T>
					</p>
				</div>

				<SocialSignInAndUp />

				<form
					id="sign-in"
					className="space-y-4"
					onSubmit={(e) => {
						console.log("submit");
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
				>
				<form.AppField
					name="email"
					listeners={{
						onChange: ({ value }) => {
							onChangeEmail?.(value);
						},
					}}
				>
					{(field) => <field.Input label={LL.FORM_FIELDS_EMAIL_LABEL()} placeholder={LL.FORM_FIELDS_EMAIL_PLACEHOLDER()} />}
				</form.AppField>
				<form.AppField name="password">
					{(field) => (
						<field.Password
							label={LL.FORM_FIELDS_PASSWORD_LABEL()}
							placeholder={LL.FORM_FIELDS_PASSWORD_PLACEHOLDER()}
							autoComplete="current-password"
						/>
					)}
				</form.AppField>

					<div className="flex">
						<Link
							from="/auth"
							search={{ show: "forgot-password" }}
							className="text-muted-foreground hover:text-foreground text-sm underline"
							// Not possible that the browser doesn't prompt the user to store a password when clicked
						>
							<T>{LL.FORGOT_PASSWORD()}</T>
						</Link>
					</div>
				</form>

				{invitationJoin && <InvitationJoinCheckbox {...invitationJoin} />}

				<div className="flex flex-col space-y-4">
					<form.AppForm>
						<form.SubmitButton className="w-full" form="sign-in">
							<T>{LL.SIGN_IN_SUBMIT_BUTTON()}</T>
						</form.SubmitButton>
					</form.AppForm>
					<Link className="text-foreground underline" from="/auth" search={{ show: "sign-up" }}>
						<p className="text-center text-sm">
							<T>{LL.SIGN_IN_NO_ACCOUNT_TEXT()}</T>
						</p>
					</Link>
				</div>
			</div>
		</>
	);
};
